// SKIM Upload Page - Unified upload, processing, and review flow
// Uses @awsui/components-react and getCookie('idToken') from ../utils/auth

import React, { useState, useEffect, useRef } from 'react';
import {
  Container,
  Header,
  Button,
  Alert,
  ProgressBar,
  Box,
  SpaceBetween,
  StatusIndicator,
  TokenGroup,
  Select,
  Input,
  Spinner,
  ExpandableSection,
  Table,
} from '@awsui/components-react';
import { skimApi } from './skimApi';
import { config } from '../utils/config';
import SkimVerificationPanel from './SkimVerificationPanel';

// Feature flag — verification UI only renders when explicitly enabled in runtime-config.
// Gamma's config lacks this flag, so gamma portal is unchanged.
const VERIFY_ENABLED = config.skimVerifyEnabled ? config.skimVerifyEnabled() : false;

const MAX_FILE_SIZE_MB = 250;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const PROCESSING_MESSAGES = [
  'Document received. AI agent is analyzing your research...',
  'Extracting competitor insights...',
  'Assessing research depth...',
  'Almost done, finalizing tags...',
];

const COMPETITOR_OPTIONS = [
  { label: 'Azure', value: 'Azure' },
  { label: 'GCP', value: 'GCP' },
  { label: 'OCI (Oracle Cloud)', value: 'OCI' },
  { label: 'Alibaba Cloud', value: 'Alibaba' },
  { label: 'Snowflake', value: 'Snowflake' },
  { label: 'Databricks', value: 'Databricks' },
  { label: 'MongoDB', value: 'MongoDB' },
  { label: 'Confluent', value: 'Confluent' },
  { label: 'Elastic', value: 'Elastic' },
  { label: 'Splunk', value: 'Splunk' },
  { label: 'Datadog', value: 'Datadog' },
  { label: 'Custom (type below)', value: 'custom' },
];

const RESEARCH_TYPE_OPTIONS = [
  { label: 'Product Analysis', value: 'Product Analysis' },
  { label: 'Market Analysis', value: 'Market Analysis' },
  { label: 'Executive Brief', value: 'Executive Brief' },
  { label: 'SpecReq', value: 'SpecReq' },
  { label: 'CSS', value: 'CSS' },
  { label: 'Custom (type below)', value: 'custom' },
];

const DEPTH_OPTIONS = [
  { label: '-- Not specified --', value: '' },
  { label: '100 - High-level overview', value: '100' },
  { label: '200 - Moderate detail', value: '200' },
  { label: '300 - Detailed analysis', value: '300' },
  { label: '400 - Deep technical dive', value: '400' },
];

// Steps: 'select' | 'uploading' | 'notification' | 'processing' | 'review' | 'published'
// For checked docs: verification runs in parallel with review; results appear inline via SkimVerificationPanel.
// document_type: 'attested' (observation, 30d archive) | 'checked' (maintained, verify required)
// F72: the upload box lost its files when the author visited My Uploads and came
// back — App.js unmounts this page on nav, and component state dies with it.
// File objects can't be serialized to storage, so they're stashed module-scope:
// they survive navigation within the tab session and are cleared when the upload
// actually starts (or the tab reloads — same as before).
let stashedFiles = [];

const SkimUploadPage = ({ onNavigate, batchId: initialBatchId }) => {
  const [step, setStep] = useState('select');
  const [selectedFiles, setSelectedFiles] = useState(() => stashedFiles);
  const [dragActive, setDragActive] = useState(false);
  const [error, setError] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [additionalContributors, setAdditionalContributors] = useState('');

  // Processing state
  const [batchId, setBatchId] = useState(null);
  const [jobIds, setJobIds] = useState([]);
  const [processingMessageIndex, setProcessingMessageIndex] = useState(0);
  const [wantNotification, setWantNotification] = useState(false);
  const [notificationShown, setNotificationShown] = useState(false);

  // Review state
  const [jobs, setJobs] = useState([]);
  const [editedTags, setEditedTags] = useState({});
  const [newCompetitor, setNewCompetitor] = useState({});
  const [showCustomInput, setShowCustomInput] = useState({});
  const [newProductName, setNewProductName] = useState({});
  const [showProductNameInput, setShowProductNameInput] = useState({});
  const [showCustomResearchType, setShowCustomResearchType] = useState({});
  const [customResearchType, setCustomResearchType] = useState({});
  const [publishing, setPublishing] = useState(false);
  const [publishSuccess, setPublishSuccess] = useState(false);
  const [editedManifest, setEditedManifest] = useState({});
  // R4: entities the author adds because the detector missed them.
  const [addedEntities, setAddedEntities] = useState({}); // jobId -> [{original, replacement, entity_type}]
  const [addEntityName, setAddEntityName] = useState('');
  const [addEntityReplacement, setAddEntityReplacement] = useState('');
  const [addEntityType, setAddEntityType] = useState({ label: 'Organization', value: 'organization' });
  // R4 symmetry: entities the author unmasks because the detector over-masked
  // (e.g. FSI — an industry acronym, not a company). jobId -> [originals]
  const [removedEntities, setRemovedEntities] = useState({});
  const [savingMasking, setSavingMasking] = useState(false);
  const [maskingSaved, setMaskingSaved] = useState(false);
  const [maskingReviewed, setMaskingReviewed] = useState({});

  // Verification state
  const [documentType, setDocumentType] = useState(null); // 'attested' | 'checked'
  const [verificationResults, setVerificationResults] = useState({}); // keyed by job_id
  const [verifyPolling, setVerifyPolling] = useState(new Set()); // Set of job_ids being polled
  const [flagOverrideInputs, setFlagOverrideInputs] = useState({}); // {sectionIndex: ''}
  const [showOverrideInput, setShowOverrideInput] = useState({}); // {sectionIndex: bool}

  const pollingRef = useRef(null);
  const messageIntervalRef = useRef(null);

  // If batchId prop is passed (e.g., from History page Review button), load directly into review
  // F72: mirror the picker into the module stash (cleared on upload start).
  useEffect(() => {
    if (step === 'select') {
      stashedFiles = selectedFiles;
    }
  }, [selectedFiles, step]);

  useEffect(() => {
    if (!initialBatchId) return;

    const loadBatchForReview = async () => {
      try {
        setStep('processing');
        const batchData = await skimApi.getBatchStatus(initialBatchId);
        const jobStatuses = await Promise.all(
          batchData.files.map((file) => skimApi.getJobStatus(file.job_id))
        );

        const pendingJobs = jobStatuses.filter((j) => j.status === 'PENDING_REVIEW');
        if (pendingJobs.length === 0) {
          setError('No files pending review in this batch.');
          setStep('select');
          return;
        }

        setJobs(pendingJobs);
        const initialTags = {};
        pendingJobs.forEach((job) => {
          if (job.suggested_tags) {
            initialTags[job.job_id] = {
              research_type: job.suggested_tags.research_type,
              competitors: job.suggested_tags.competitors || [],
              products_and_services: job.suggested_tags.products_and_services || [],
              research_depth: job.suggested_tags.research_depth,
            };
          }
        });
        setEditedTags(initialTags);
        // Initialize maskingReviewed from the API response for jobs already reviewed
        const initialMaskingReviewed = {};
        pendingJobs.forEach((job) => {
          if (job.masking_reviewed === true) {
            initialMaskingReviewed[job.job_id] = true;
          }
        });
        setMaskingReviewed(initialMaskingReviewed);
        setStep('review');
      } catch (err) {
        console.error('Error loading batch for review:', err);
        setError('Could not load batch for review. Please try again.');
        setStep('select');
      }
    };

    loadBatchForReview();
  }, [initialBatchId]);

  // Rotate processing messages — each message shows once, then stays on the last one
  useEffect(() => {
    if (step === 'processing') {
      messageIntervalRef.current = setInterval(() => {
        setProcessingMessageIndex((prev) => {
          if (prev < PROCESSING_MESSAGES.length - 1) return prev + 1;
          clearInterval(messageIntervalRef.current);
          return prev;
        });
      }, 4000);
    }
    return () => {
      if (messageIntervalRef.current) clearInterval(messageIntervalRef.current);
    };
  }, [step]);

  // Poll for job status during processing
  useEffect(() => {
    if (step !== 'processing' || jobIds.length === 0) return;

    const pollStatus = async () => {
      try {
        const jobStatuses = await Promise.all(jobIds.map((id) => skimApi.getJobStatus(id)));
        const allDone = jobStatuses.every(
          (j) => j.status === 'PENDING_REVIEW' || j.status === 'PUBLISHED' || j.status === 'FAILED'
        );

        if (allDone) {
          if (pollingRef.current) clearInterval(pollingRef.current);

          const failed = jobStatuses.filter((j) => j.status === 'FAILED');
          if (failed.length === jobStatuses.length) {
            setError('Processing failed. Please try uploading again.');
            setStep('select');
            return;
          }

          // Show notification when processing completes
          if (!notificationShown) {
            setNotificationShown(true);
            if (wantNotification) {
              // Browser notification if permitted
              if (typeof Notification !== 'undefined' && Notification.permission === 'granted') {
                new Notification('SKIM', { body: 'Your document is ready for review!' });
              }
              // Always show alert popup as fallback
              alert('✅ Your document is ready for review!');
            }
          }

          const completedJobs = jobStatuses.filter((j) => j.status === 'PENDING_REVIEW');

          if (VERIFY_ENABLED && documentType === 'checked' && completedJobs.length > 0) {
            // Go straight to review; fire verification in parallel.
            // The verification panel appears inline in the review step when results arrive.
            const initialTags = {};
            completedJobs.forEach((job) => {
              if (job.suggested_tags) {
                initialTags[job.job_id] = {
                  research_type: job.suggested_tags.research_type,
                  competitors: job.suggested_tags.competitors || [],
                  products_and_services: job.suggested_tags.products_and_services || [],
                  research_depth: job.suggested_tags.research_depth,
                };
              }
            });
            setJobs(completedJobs);
            setEditedTags(initialTags);
            setStep('review');

            // Fire verification in background — don't await
            completedJobs.forEach((job) => {
              skimApi.triggerVerify(job.job_id).catch((e) => {
                console.log('Verify trigger returned (may have timed out):', e.message);
              });
              pollVerificationResults(job.job_id);
            });
          } else {
            // Attested or non-verify-enabled: go straight to review
            // Move to review
            const initialTags = {};
            completedJobs.forEach((job) => {
              if (job.suggested_tags) {
                initialTags[job.job_id] = {
                  research_type: job.suggested_tags.research_type,
                  competitors: job.suggested_tags.competitors || [],
                  products_and_services: job.suggested_tags.products_and_services || [],
                  research_depth: job.suggested_tags.research_depth,
                };
              }
            });
            setJobs(completedJobs);
            setEditedTags(initialTags);
            setStep('review');
          }
        }
      } catch (err) {
        console.error('Polling error:', err);
      }
    };

    pollStatus();
    pollingRef.current = setInterval(pollStatus, 3000);

    return () => {
      if (pollingRef.current) clearInterval(pollingRef.current);
    };
  }, [step, jobIds, wantNotification]);

  // File handling
  const handleFileSelect = (event) => {
    const files = Array.from(event.target.files || []);
    addFiles(files);
  };

  const addFiles = (files) => {
    const validFiles = files.filter((file) => {
      const ext = file.name.split('.').pop()?.toLowerCase();
      if (ext !== 'docx' && ext !== 'pptx' && ext !== 'pdf') return false;
      if (file.size > MAX_FILE_SIZE_BYTES) {
        setError(`"${file.name}" exceeds the ${MAX_FILE_SIZE_MB} MB file size limit.`);
        return false;
      }
      return true;
    });
    const newFiles = [...selectedFiles, ...validFiles].slice(0, 10);
    setSelectedFiles(newFiles);
  };

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') setDragActive(true);
    else if (e.type === 'dragleave') setDragActive(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    addFiles(Array.from(e.dataTransfer.files));
  };

  const removeFile = (index) => {
    setSelectedFiles(selectedFiles.filter((_, i) => i !== index));
  };

  // Upload flow
  const handleUpload = async () => {
    if (selectedFiles.length === 0) return;
    stashedFiles = []; // F72: consumed — a later visit starts with an empty box
    setError(null);
    setStep('uploading');
    setUploadProgress(10);

    try {
      const uploadResponse = await skimApi.initiateUpload(null, selectedFiles,
        additionalContributors.split(',').map(a => a.trim()).filter(a => a.length > 0),
        VERIFY_ENABLED ? (documentType || 'attested') : undefined
      );
      setBatchId(uploadResponse.batch_id);
      setUploadProgress(30);

      // Upload files to S3
      const ids = [];
      for (let i = 0; i < uploadResponse.files.length; i++) {
        const fileInfo = uploadResponse.files[i];
        await skimApi.uploadToS3(fileInfo.presigned_url, selectedFiles[i]);
        ids.push(fileInfo.job_id);
        setUploadProgress(30 + Math.round(((i + 1) / uploadResponse.files.length) * 50));
      }

      setJobIds(ids);
      setUploadProgress(90);

      // Fire confirm for each (non-blocking)
      ids.forEach((jobId) => {
        skimApi.confirmUpload(jobId).catch((err) => {
          console.warn(`Confirm failed for ${jobId}:`, err);
        });
      });

      setUploadProgress(100);
      setStep('notification');
    } catch (err) {
      console.error('Upload error:', err);
      setError(err.message || 'Upload failed. Please check your file and try again.');
      setStep('select');
    }
  };

  // Notification choice
  const handleNotificationChoice = (notify) => {
    setWantNotification(notify);
    if (notify && Notification.permission === 'default') {
      Notification.requestPermission();
    }
    setStep('processing');
  };

  // Tag editing
  const updateResearchType = (jobId, value) => {
    setEditedTags((prev) => ({ ...prev, [jobId]: { ...prev[jobId], research_type: value } }));
  };

  const removeCompetitor = (jobId, competitor) => {
    setEditedTags((prev) => ({
      ...prev,
      [jobId]: { ...prev[jobId], competitors: prev[jobId]?.competitors?.filter((c) => c !== competitor) || [] },
    }));
  };

  const addCompetitor = (jobId, competitor) => {
    if (!competitor.trim()) return;
    const newTags = competitor.split(',').map(t => t.trim()).filter(t => t.length > 0);
    const currentTags = editedTags[jobId]?.competitors || [];
    const uniqueNew = newTags.filter(t => !currentTags.includes(t));
    if (uniqueNew.length > 0) {
      setEditedTags((prev) => ({
        ...prev,
        [jobId]: { ...prev[jobId], competitors: [...currentTags, ...uniqueNew] },
      }));
    }
    setNewCompetitor((prev) => ({ ...prev, [jobId]: '' }));
    setShowCustomInput((prev) => ({ ...prev, [jobId]: false }));
  };

  const updateResearchDepth = (jobId, depth) => {
    setEditedTags((prev) => ({ ...prev, [jobId]: { ...prev[jobId], research_depth: depth || undefined } }));
  };

  const removeProductName = (jobId, name) => {
    setEditedTags((prev) => ({
      ...prev,
      [jobId]: { ...prev[jobId], products_and_services: prev[jobId]?.products_and_services?.filter((n) => n !== name) || [] },
    }));
  };

  const addProductName = (jobId, name) => {
    if (!name.trim()) return;
    const newTags = name.split(',').map(t => t.trim()).filter(t => t.length > 0);
    const currentTags = editedTags[jobId]?.products_and_services || [];
    const uniqueNew = newTags.filter(t => !currentTags.includes(t));
    if (uniqueNew.length > 0) {
      setEditedTags((prev) => ({
        ...prev,
        [jobId]: { ...prev[jobId], products_and_services: [...currentTags, ...uniqueNew] },
      }));
    }
    setNewProductName((prev) => ({ ...prev, [jobId]: '' }));
    setShowProductNameInput((prev) => ({ ...prev, [jobId]: false }));
  };

  // Publish
  const handlePublish = async (jobId) => {
    setPublishing(true);
    setError(null);
    try {
      await skimApi.publishJob(jobId, editedTags[jobId] || {});
      setJobs((prev) => prev.filter((j) => j.job_id !== jobId));
      if (jobs.length <= 1) {
        setPublishSuccess(true);
        setStep('published');
      }
    } catch (err) {
      console.error('Publish error:', err);
      setError(err.message || 'Failed to publish. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  const handlePublishAll = async () => {
    setPublishing(true);
    setError(null);
    try {
      // For checked docs, skip any job that is still blocked by verification
      const publishable = jobs.filter((job) => {
        const jobResults = verificationResults[job.job_id];
        const vBlocked = VERIFY_ENABLED && documentType === 'checked' && (
          verifyPolling.has(job.job_id) ||
          (jobResults && !jobResults._timeout && (jobResults.sections || []).some(
            s => !s.overridden && !s.applied && !s.fix_myself
          ))
        );
        return !vBlocked;
      });
      if (publishable.length === 0) {
        setError('No documents can be published yet — verification is still pending or unresolved.');
        setPublishing(false);
        return;
      }
      await Promise.all(publishable.map((job) => skimApi.publishJob(job.job_id, editedTags[job.job_id] || {})));
      setPublishSuccess(true);
      setStep('published');
    } catch (err) {
      console.error('Publish error:', err);
      setError(err.message || 'Failed to publish. Please try again.');
    } finally {
      setPublishing(false);
    }
  };

  // R4: add a missed entity to the pending additions for this job
  const handleAddEntity = (jobId) => {
    const name = addEntityName.trim();
    const replacement = addEntityReplacement.trim();
    if (!name || !replacement) return;
    setAddedEntities(prev => ({
      ...prev,
      [jobId]: [...(prev[jobId] || []), { original: name, replacement, entity_type: addEntityType.value }],
    }));
    setAddEntityName('');
    setAddEntityReplacement('');
  };

  // R4 symmetry: queue an over-masked entity for removal (saved with the manifest)
  const handleMarkUnmask = (jobId, original) => {
    setRemovedEntities(prev => ({
      ...prev,
      [jobId]: [...(prev[jobId] || []), original],
    }));
  };

  // Save masking edits (R4: via the manifest endpoint — the server re-applies the
  // manifest over the UNMASKED original and regenerates the masked content, so
  // replacement edits and added entities both persist in the manifest. The old
  // path did a client-side string swap on the masked text and never taught the
  // manifest.)
  const handleSaveMasking = async (jobId) => {
    setSavingMasking(true);
    setError(null); // clear any stale publish-gate error from a pre-review Publish attempt
    try {
      const job = jobs.find(j => j.job_id === jobId);
      if (!job || !job.masking_manifest) return;

      const edited = editedManifest[jobId];
      // Review finding (rev 3, F27a-class): building the manifest from ONLY
      // organizations/persons/financial_redactions dropped kept_organizations /
      // kept_persons on every save — the server stores whatever is sent, so the
      // agent's record of what it deliberately left unmasked vanished and
      // kept_entities went empty again (the exact invisibility F27a fixed).
      // Spread the stored manifest first: unknown/kept_ keys survive every save.
      const manifest = {
        ...job.masking_manifest,
        organizations: (edited?.organizations || job.masking_manifest.organizations || [])
          .map(({ original, replacement }) => ({ original, replacement })),
        persons: (edited?.persons || job.masking_manifest.persons || [])
          .map(({ original, replacement }) => ({ original, replacement })),
        financial_redactions: !!job.masking_manifest.financial_redactions,
      };
      const removed = (removedEntities[jobId] || []).map((r) => r.toLowerCase());
      manifest.organizations = manifest.organizations.filter(
        (o) => !removed.includes(o.original.toLowerCase()));
      manifest.persons = manifest.persons.filter(
        (p) => !removed.includes(p.original.toLowerCase()));
      (addedEntities[jobId] || []).forEach((e) => {
        const target = e.entity_type === 'person' ? manifest.persons : manifest.organizations;
        // never a silent duplicate
        if (!target.some((x) => x.original.toLowerCase() === e.original.toLowerCase())) {
          target.push({ original: e.original, replacement: e.replacement });
        }
      });

      await skimApi.saveMaskingManifest(jobId, manifest);
      // The server regenerated the masked content — refetch for the real text.
      const fresh = await skimApi.getJobStatus(jobId);
      setMaskingSaved(true);
      setMaskingReviewed((prev) => ({ ...prev, [jobId]: true }));
      setAddedEntities((prev) => ({ ...prev, [jobId]: [] }));
      setRemovedEntities((prev) => ({ ...prev, [jobId]: [] }));
      setJobs(prev => prev.map(j => j.job_id === jobId
        ? { ...j, markdown_content: fresh.markdown_content ?? j.markdown_content,
            masking_manifest: fresh.masking_manifest ?? manifest }
        : j));
    } catch (err) {
      console.error('Error saving masking:', err);
      setError(err.message || 'Failed to save masking changes.');
    } finally {
      setSavingMasking(false);
    }
  };

  const pollVerificationResults = async (jobId) => {
    setVerifyPolling((prev) => new Set([...prev, jobId]));
    const maxAttempts = 30;
    for (let i = 0; i < maxAttempts; i++) {
      await new Promise(r => setTimeout(r, 10000));
      try {
        const result = await skimApi.getVerificationResults(jobId);
        if (result && result.verification_results) {
          setVerificationResults((prev) => ({ ...prev, [jobId]: result.verification_results }));
          setVerifyPolling((prev) => { const s = new Set(prev); s.delete(jobId); return s; });
          return;
        }
      } catch (e) {
        // Not ready yet — keep polling
      }
    }
    // Timeout — mark with _timeout sentinel so the UI can show a warning
    setVerificationResults((prev) => ({ ...prev, [jobId]: { _timeout: true, sections: [] } }));
    setVerifyPolling((prev) => { const s = new Set(prev); s.delete(jobId); return s; });
  };

  const resetFlow = () => {
    setStep('select');
    setSelectedFiles([]);
    setError(null);
    setUploadProgress(0);
    setBatchId(null);
    setJobIds([]);
    setJobs([]);
    setEditedTags({});
    setPublishSuccess(false);
    setNotificationShown(false);
    setProcessingMessageIndex(0);
    setAdditionalContributors('');
    setEditedManifest({});
    setMaskingSaved(false);
    setMaskingReviewed({});
    setDocumentType(null);
    setVerificationResults({});
    setVerifyPolling(new Set());
    setFlagOverrideInputs({});
    setShowOverrideInput({});
  };

  // ─── RENDER ───────────────────────────────────────────────────────────────────

  return (
    <Container
      header={
        <Header variant="h1" description="Upload DOCX or PPTX files for AI-powered analysis and tagging">
          Upload Research Documents
        </Header>
      }
    >
      <SpaceBetween direction="vertical" size="l">
        {/* Error */}
        {error && (
          <Alert type="error" dismissible onDismiss={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Notification popup */}
        {notificationShown && step === 'review' && (
          <Alert type="success" dismissible onDismiss={() => setNotificationShown(false)}>
            Your document is ready for review!
          </Alert>
        )}

        {/* ─── STEP 1: FILE SELECT ─── */}
        {step === 'select' && (
          <Container>
            <SpaceBetween direction="vertical" size="l">
              <Header variant="h2">Select Files</Header>

              {/* Document Type — only when verify feature is enabled */}
              {VERIFY_ENABLED && (
                <div>
                  <Box fontWeight="bold" fontSize="heading-xs" margin={{ bottom: 'xs' }}>Document Type</Box>
                  <SpaceBetween direction="horizontal" size="m">
                    <div
                      style={{
                        border: `2px solid ${documentType === 'attested' ? '#ff9900' : 'var(--color-border-divider-default)'}`,
                        borderRadius: '8px', padding: '16px', cursor: 'pointer', minWidth: '200px',
                        background: documentType === 'attested' ? '#fff8ef' : 'white',
                      }}
                      onClick={() => setDocumentType('attested')}
                    >
                      <Box fontWeight="bold" fontSize="body-m">Point-in-time</Box>
                      <Box color="text-body-secondary" fontSize="body-s">
                        Meeting notes, one-off insights. Auto-archives in 30 days. No maintenance.
                      </Box>
                    </div>
                    <div
                      style={{
                        border: `2px solid ${documentType === 'checked' ? '#ff9900' : 'var(--color-border-divider-default)'}`,
                        borderRadius: '8px', padding: '16px', cursor: 'pointer', minWidth: '200px',
                        background: documentType === 'checked' ? '#fff8ef' : 'white',
                      }}
                      onClick={() => setDocumentType('checked')}
                    >
                      <Box fontWeight="bold" fontSize="body-m">Maintained Content</Box>
                      <Box color="text-body-secondary" fontSize="body-s">
                        Competitive analyses, pricing insights. Verified before publishing.
                      </Box>
                    </div>
                  </SpaceBetween>
                </div>
              )}

              {/* Drag and Drop Zone */}
              <div
                style={{
                  position: 'relative',
                  border: dragActive ? '2px dashed var(--color-text-link-default)' : '2px dashed var(--color-border-divider-default)',
                  borderRadius: '8px',
                  padding: '32px',
                  textAlign: 'center',
                  backgroundColor: dragActive ? 'var(--color-background-layout-toggle-active)' : 'var(--color-background-container-content)',
                  cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <input
                  type="file"
                  multiple
                  accept=".docx,.pptx,.pdf"
                  onChange={handleFileSelect}
                  style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', opacity: 0, cursor: 'pointer' }}
                />
                <SpaceBetween direction="vertical" size="xs">
                  <Box variant="p" color="text-body-secondary">
                    <strong>Click to upload</strong> or drag and drop
                  </Box>
                  <Box variant="small" color="text-body-secondary">
                    DOCX, PPTX, or PDF files only (max 10 files, 250 MB each)
                  </Box>
                </SpaceBetween>
              </div>

              {/* Selected Files List */}
              {selectedFiles.length > 0 && (
                <div style={{ border: '1px solid var(--color-border-divider-default)', borderRadius: '8px', overflow: 'hidden' }}>
                  {selectedFiles.map((file, index) => (
                    <div
                      key={index}
                      style={{
                        display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                        padding: '12px',
                        borderBottom: index < selectedFiles.length - 1 ? '1px solid var(--color-border-divider-default)' : 'none',
                      }}
                    >
                      <SpaceBetween direction="horizontal" size="xs">
                        <Box variant="span">{file.name}</Box>
                        <Box variant="small" color="text-body-secondary">({(file.size / 1024 / 1024).toFixed(2)} MB)</Box>
                      </SpaceBetween>
                      <Button onClick={() => removeFile(index)} variant="icon" iconName="close" />
                    </div>
                  ))}
                </div>
              )}

              {/* Upload Button */}
              <Box>
                <Box fontWeight="bold" fontSize="heading-xs" margin={{ bottom: 'xs' }}>Additional Contributors (optional)</Box>
                <Input
                  value={additionalContributors}
                  onChange={({ detail }) => setAdditionalContributors(detail.value)}
                  placeholder="e.g. chujunz, rjaisun, bellackj (comma-separated aliases)"
                />
                <Box variant="small" color="text-body-secondary" margin={{ top: 'xxs' }}>
                  Add Amazon aliases of other contributors to this research
                </Box>
              </Box>

              <Box float="right">
                <SpaceBetween direction="vertical" size="xs">
                  {/* R12: the route must be a deliberate choice — no silent default to
                      Point-in-time. Gate ONLY when the chooser is rendered: Gamma runs with
                      VERIFY_ENABLED=false and shows no cards, so an unconditional check
                      would block every Gamma upload. */}
                  {VERIFY_ENABLED && selectedFiles.length > 0 && !documentType && (
                    <Box color="text-status-warning" fontSize="body-s">
                      Choose a Document Type above to continue.
                    </Box>
                  )}
                  <Button
                    onClick={handleUpload}
                    disabled={selectedFiles.length === 0 || (VERIFY_ENABLED && !documentType)}
                    variant="primary"
                  >
                    Upload and Process
                  </Button>
                </SpaceBetween>
              </Box>
            </SpaceBetween>
          </Container>
        )}

        {/* ─── STEP 2: UPLOADING ─── */}
        {step === 'uploading' && (
          <Container>
            <SpaceBetween direction="vertical" size="m">
              <Box variant="h3">Uploading your document...</Box>
              <ProgressBar value={uploadProgress} />
            </SpaceBetween>
          </Container>
        )}

        {/* ─── STEP 3: NOTIFICATION PROMPT ─── */}
        {step === 'notification' && (
          <Container>
            <SpaceBetween direction="vertical" size="l">
              <Alert type="info">
                Large documents may take a few minutes to process. Would you like a notification when it's ready?
              </Alert>
              <SpaceBetween direction="horizontal" size="m">
                <Button onClick={() => handleNotificationChoice(true)} variant="primary">
                  Yes, notify me
                </Button>
                <Button onClick={() => handleNotificationChoice(false)}>
                  No, I'll wait here
                </Button>
              </SpaceBetween>
            </SpaceBetween>
          </Container>
        )}

        {/* ─── STEP 4: PROCESSING ─── */}
        {step === 'processing' && (
          <Container>
            <Box textAlign="center" padding={{ vertical: 'xxl' }}>
              <SpaceBetween direction="vertical" size="m">
                <Spinner size="large" />
                <Box variant="h3">{PROCESSING_MESSAGES[processingMessageIndex]}</Box>
                <Box variant="small" color="text-body-secondary">
                  This usually takes 1-3 minutes depending on document size.
                </Box>
              </SpaceBetween>
            </Box>
          </Container>
        )}

        {/* ─── STEP 5: REVIEW ─── */}
        {step === 'review' && jobs.length > 0 && (
          <SpaceBetween direction="vertical" size="l">
            {jobs.map((job) => {
              const tags = editedTags[job.job_id] || { competitors: [], products_and_services: [], research_depth: '' };
              // Gate: masking-applied jobs must be reviewed before publishing
              const maskingBlocked = job.masking_applied && !maskingReviewed[job.job_id];
              // For checked docs: also block publish until verification is done and all flags resolved
              const jobResults = verificationResults[job.job_id];
              const verifyBlocked = VERIFY_ENABLED && documentType === 'checked' && (
                verifyPolling.has(job.job_id) ||
                (jobResults && !jobResults._timeout && (jobResults.sections || []).some(
                  s => !s.overridden && !s.applied && !s.fix_myself
                ))
              );
              return (
                <Container
                  key={job.job_id}
                  header={
                    <Header variant="h2" actions={
                      <SpaceBetween direction="horizontal" size="xs">
                        {maskingReviewed[job.job_id] && (
                          <Box variant="small" color="text-status-success">
                            ✓ Masking reviewed
                          </Box>
                        )}
                        {maskingBlocked && (
                          <Box variant="small" color="text-status-warning">
                            Review masking before publishing
                          </Box>
                        )}
                        {VERIFY_ENABLED && documentType === 'checked' && verifyPolling.has(job.job_id) && (
                          <Box variant="small" color="text-status-warning">
                            Verification in progress...
                          </Box>
                        )}
                        {verifyBlocked && !verifyPolling.has(job.job_id) && (
                          <Box variant="small" color="text-status-warning">
                            Resolve verification flags before publishing
                          </Box>
                        )}
                        <Button
                          onClick={() => handlePublish(job.job_id)}
                          variant="primary"
                          loading={publishing}
                          disabled={maskingBlocked || verifyBlocked}
                        >
                          Publish
                        </Button>
                      </SpaceBetween>
                    }>
                      {job.filename}
                    </Header>
                  }
                >
                  <SpaceBetween direction="vertical" size="l">
                    {/* Attested path — 30-day archive notice */}
                    {VERIFY_ENABLED && documentType === 'attested' && (
                      <Alert type="info">
                        This document is submitted as firsthand research. It will auto-archive after 30 days.
                        You can extend or convert to Maintained Content from Upload History.
                      </Alert>
                    )}

                    {/* Inline verification panel — appears when results arrive; runs parallel to tag review */}
                    {VERIFY_ENABLED && documentType === 'checked' && (
                      verifyPolling.has(job.job_id) ? (
                        <Alert type="info">
                          <SpaceBetween direction="horizontal" size="xs">
                            <Spinner size="normal" />
                            <Box>Fact-checking your document in the background... Publish will be available once complete.</Box>
                          </SpaceBetween>
                        </Alert>
                      ) : verificationResults[job.job_id] && verificationResults[job.job_id]._timeout ? (
                        <Alert type="warning">
                          Fact-checking timed out — the verification service did not respond in time.
                          You may publish, but this document has not been fact-checked.
                        </Alert>
                      ) : verificationResults[job.job_id] ? (
                        <SkimVerificationPanel
                          jobId={job.job_id}
                          sections={verificationResults[job.job_id].sections || []}
                          onSectionsChange={(updated) =>
                            setVerificationResults((prev) => ({
                              ...prev,
                              [job.job_id]: { ...prev[job.job_id], sections: updated },
                            }))
                          }
                          onComplete={null}
                          completeLabel={null}
                          verifyEnabled={VERIFY_ENABLED}
                          hideCompleteButton={true}
                        />
                      ) : null
                    )}

                    {/* Masking Summary — Editable */}
                    {job.masking_applied && job.masking_manifest && (
                      <ExpandableSection headerText="⚠️ Sensitive content was automatically masked — click to see details">
                        <SpaceBetween direction="vertical" size="s">
                          {maskingSaved && (
                            <Alert type="success" dismissible onDismiss={() => setMaskingSaved(false)}>
                              Masking changes saved successfully
                            </Alert>
                          )}

                          {/* Organizations */}
                          {job.masking_manifest.organizations?.length > 0 && (
                            <Table
                              header={<Header variant="h3">Companies / Organizations</Header>}
                              columnDefinitions={[
                                { id: 'original', header: 'Original', cell: (item, idx) => item.original },
                                { id: 'replacement', header: 'Replaced With (editable)', cell: (item, idx) => (
                                  <Input
                                    value={(editedManifest[job.job_id]?.organizations?.[job.masking_manifest.organizations.indexOf(item)]?.replacement) ?? item.replacement}
                                    onChange={({ detail }) => {
                                      const itemIdx = job.masking_manifest.organizations.indexOf(item);
                                      setEditedManifest(prev => {
                                        const jobManifest = prev[job.job_id] || { organizations: [...job.masking_manifest.organizations], persons: [...(job.masking_manifest.persons || [])] };
                                        const orgs = [...jobManifest.organizations];
                                        orgs[itemIdx] = { ...orgs[itemIdx], replacement: detail.value };
                                        return { ...prev, [job.job_id]: { ...jobManifest, organizations: orgs } };
                                      });
                                    }}
                                  />
                                )},
                                { id: 'count', header: 'Occurrences', cell: item => item.count },
                                { id: 'unmask', header: '', cell: item => (
                                  <Button
                                    onClick={() => handleMarkUnmask(job.job_id, item.original)}
                                    variant="inline-link"
                                    fontSize="body-s"
                                  >
                                    Unmask
                                  </Button>
                                )},
                              ]}
                              items={(job.masking_manifest.organizations || []).filter(
                                (item) => !(removedEntities[job.job_id] || []).includes(item.original))}
                              variant="embedded"
                            />
                          )}

                          {/* Persons */}
                          {job.masking_manifest.persons?.length > 0 && (
                            <Table
                              header={<Header variant="h3">People</Header>}
                              columnDefinitions={[
                                { id: 'original', header: 'Original', cell: item => item.original },
                                { id: 'replacement', header: 'Replaced With (editable)', cell: (item, idx) => (
                                  <Input
                                    value={(editedManifest[job.job_id]?.persons?.[job.masking_manifest.persons.indexOf(item)]?.replacement) ?? item.replacement}
                                    onChange={({ detail }) => {
                                      const itemIdx = job.masking_manifest.persons.indexOf(item);
                                      setEditedManifest(prev => {
                                        const jobManifest = prev[job.job_id] || { organizations: [...(job.masking_manifest.organizations || [])], persons: [...job.masking_manifest.persons] };
                                        const persons = [...jobManifest.persons];
                                        persons[itemIdx] = { ...persons[itemIdx], replacement: detail.value };
                                        return { ...prev, [job.job_id]: { ...jobManifest, persons: persons } };
                                      });
                                    }}
                                  />
                                )},
                                { id: 'count', header: 'Occurrences', cell: item => item.count },
                                { id: 'unmask', header: '', cell: item => (
                                  <Button
                                    onClick={() => handleMarkUnmask(job.job_id, item.original)}
                                    variant="inline-link"
                                    fontSize="body-s"
                                  >
                                    Unmask
                                  </Button>
                                )},
                              ]}
                              items={(job.masking_manifest.persons || []).filter(
                                (item) => !(removedEntities[job.job_id] || []).includes(item.original))}
                              variant="embedded"
                            />
                          )}

                          {/* Financial */}
                          {job.masking_manifest.financial_redactions && (
                            <Alert type="info">
                              Financial values were redacted from this document.
                            </Alert>
                          )}

                          {/* R4: add an entity the detector missed */}
                          <Box fontWeight="bold" fontSize="heading-xs">Add a missed entity</Box>
                          <SpaceBetween direction="horizontal" size="xs">
                            <Input
                              placeholder="Name as it appears (e.g. Hyundai)"
                              value={addEntityName}
                              onChange={({ detail }) => setAddEntityName(detail.value)}
                            />
                            <Input
                              placeholder="Mask as (e.g. an Automotive Company)"
                              value={addEntityReplacement}
                              onChange={({ detail }) => setAddEntityReplacement(detail.value)}
                            />
                            <Select
                              selectedOption={addEntityType}
                              onChange={({ detail }) => setAddEntityType(detail.selectedOption)}
                              options={[
                                { label: 'Organization', value: 'organization' },
                                { label: 'Person', value: 'person' },
                              ]}
                            />
                            <Button
                              onClick={() => handleAddEntity(job.job_id)}
                              disabled={!addEntityName.trim() || !addEntityReplacement.trim()}
                            >
                              Add
                            </Button>
                          </SpaceBetween>
                          {((addedEntities[job.job_id] || []).length > 0 || (removedEntities[job.job_id] || []).length > 0) && (
                            <Box variant="small">
                              Pending (saved with "Save Masking Changes"):{' '}
                              {[
                                ...(addedEntities[job.job_id] || []).map((e) => `mask ${e.original} → ${e.replacement}`),
                                ...(removedEntities[job.job_id] || []).map((r) => `unmask ${r}`),
                              ].join(' · ')}
                            </Box>
                          )}
                          {/* Save button */}
                          <Button onClick={() => handleSaveMasking(job.job_id)} loading={savingMasking} variant="normal">
                            Save Masking Changes
                          </Button>
                        </SpaceBetween>
                      </ExpandableSection>
                    )}

                    {/* Research Type */}
                    <div>
                      <Box fontWeight="bold" fontSize="heading-xs" margin={{ bottom: 'xs' }}>Research Type</Box>
                      <Select
                        selectedOption={
                          RESEARCH_TYPE_OPTIONS.find((opt) => opt.value === tags.research_type) ||
                          { label: tags.research_type || 'Not specified', value: tags.research_type || '' }
                        }
                        onChange={({ detail }) => {
                          if (detail.selectedOption.value === 'custom') {
                            setShowCustomResearchType((prev) => ({ ...prev, [job.job_id]: true }));
                          } else {
                            updateResearchType(job.job_id, detail.selectedOption.value);
                          }
                        }}
                        options={RESEARCH_TYPE_OPTIONS}
                        selectedAriaLabel="Selected"
                      />
                      {showCustomResearchType[job.job_id] && (
                        <Box margin={{ top: 's' }}>
                          <SpaceBetween direction="horizontal" size="xs">
                            <Input
                              value={customResearchType[job.job_id] || ''}
                              onChange={({ detail }) => setCustomResearchType((prev) => ({ ...prev, [job.job_id]: detail.value }))}
                              placeholder="Enter custom research type"
                            />
                            <Button onClick={() => { updateResearchType(job.job_id, customResearchType[job.job_id] || ''); setShowCustomResearchType((prev) => ({ ...prev, [job.job_id]: false })); }} variant="primary">Set</Button>
                            <Button onClick={() => setShowCustomResearchType((prev) => ({ ...prev, [job.job_id]: false }))}>Cancel</Button>
                          </SpaceBetween>
                        </Box>
                      )}
                    </div>

                    {/* Competitors */}
                    <div>
                      <Box fontWeight="bold" fontSize="heading-xs" margin={{ bottom: 'xs' }}>Competitors</Box>
                      {tags.competitors && tags.competitors.length > 0 ? (
                        <TokenGroup
                          items={tags.competitors.map((comp) => ({ label: comp, dismissLabel: `Remove ${comp}` }))}
                          onDismiss={({ detail: { itemIndex } }) => removeCompetitor(job.job_id, tags.competitors[itemIndex])}
                        />
                      ) : (
                        <Box color="text-status-inactive" margin={{ bottom: 's' }}>No competitors identified</Box>
                      )}
                      <Box margin={{ top: 's' }}>
                        <Select
                          placeholder="-- Add Competitor --"
                          options={COMPETITOR_OPTIONS}
                          onChange={({ detail }) => {
                            if (detail.selectedOption.value === 'custom') {
                              setShowCustomInput((prev) => ({ ...prev, [job.job_id]: true }));
                            } else if (detail.selectedOption.value) {
                              addCompetitor(job.job_id, detail.selectedOption.value);
                            }
                          }}
                          selectedAriaLabel="Selected"
                        />
                      </Box>
                      {showCustomInput[job.job_id] && (
                        <Box margin={{ top: 's' }}>
                          <SpaceBetween direction="horizontal" size="xs">
                            <Input
                              value={newCompetitor[job.job_id] || ''}
                              onChange={({ detail }) => setNewCompetitor((prev) => ({ ...prev, [job.job_id]: detail.value }))}
                              placeholder="Enter competitor names (comma-separated)"
                            />
                            <Button onClick={() => addCompetitor(job.job_id, newCompetitor[job.job_id] || '')} variant="primary">Add</Button>
                            <Button onClick={() => setShowCustomInput((prev) => ({ ...prev, [job.job_id]: false }))}>Cancel</Button>
                          </SpaceBetween>
                        </Box>
                      )}
                    </div>

                    {/* Products and Services */}
                    <div>
                      <Box fontWeight="bold" fontSize="heading-xs" margin={{ bottom: 'xs' }}>Products and Services</Box>
                      {tags.products_and_services && tags.products_and_services.length > 0 ? (
                        <TokenGroup
                          items={tags.products_and_services.map((name) => ({ label: name, dismissLabel: `Remove ${name}` }))}
                          onDismiss={({ detail: { itemIndex } }) => removeProductName(job.job_id, tags.products_and_services[itemIndex])}
                        />
                      ) : (
                        <Box color="text-status-inactive" margin={{ bottom: 's' }}>No products identified</Box>
                      )}
                      <Box margin={{ top: 's' }}>
                        <SpaceBetween direction="horizontal" size="xs">
                          <Input
                            value={newProductName[job.job_id] || ''}
                            onChange={({ detail }) => setNewProductName((prev) => ({ ...prev, [job.job_id]: detail.value }))}
                            placeholder="e.g. Amazon S3, Amazon Bedrock (comma-separated)"
                            onKeyDown={(e) => { if (e.detail.key === 'Enter') addProductName(job.job_id, newProductName[job.job_id] || ''); }}
                          />
                          <Button onClick={() => addProductName(job.job_id, newProductName[job.job_id] || '')} variant="normal">Add</Button>
                        </SpaceBetween>
                      </Box>
                    </div>

                    {/* Research Depth */}
                    <div>
                      <Box fontWeight="bold" fontSize="heading-xs" margin={{ bottom: 'xs' }}>Research Depth (optional)</Box>
                      <Select
                        selectedOption={DEPTH_OPTIONS.find((opt) => opt.value === tags.research_depth) || DEPTH_OPTIONS[0]}
                        onChange={({ detail }) => updateResearchDepth(job.job_id, detail.selectedOption.value)}
                        options={DEPTH_OPTIONS}
                        selectedAriaLabel="Selected"
                      />
                    </div>

                    {/* Additional Contributors (read-only) */}
                    {job.additional_contributors && job.additional_contributors.length > 0 && (
                      <div>
                        <Box fontWeight="bold" fontSize="heading-xs" margin={{ bottom: 'xs' }}>Contributors</Box>
                        <TokenGroup
                          items={job.additional_contributors.map((alias) => ({ label: alias }))}
                          readOnly
                        />
                      </div>
                    )}
                  </SpaceBetween>
                </Container>
              );
            })}

            {/* Publish All */}
            {jobs.length > 1 && (
              <Box float="right">
                <Button onClick={handlePublishAll} variant="primary" loading={publishing}>
                  Publish All ({jobs.length} files)
                </Button>
              </Box>
            )}
          </SpaceBetween>
        )}

        {/* ─── STEP 6: PUBLISHED ─── */}
        {step === 'published' && (
          <Container>
            <Box textAlign="center" padding={{ vertical: 'xxl' }}>
              <SpaceBetween direction="vertical" size="m">
                <StatusIndicator type="success">
                  <Box variant="h2">Research published successfully!</Box>
                </StatusIndicator>
                <SpaceBetween direction="horizontal" size="m">
                  <Button onClick={resetFlow} variant="primary">Upload Another</Button>
                  <Button onClick={() => onNavigate && onNavigate('skim-history')}>View My Uploads</Button>
                </SpaceBetween>
              </SpaceBetween>
            </Box>
          </Container>
        )}
      </SpaceBetween>
    </Container>
  );
};

export default SkimUploadPage;
